/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React from 'react';
import { Badge, Button } from 'reactstrap';
import '@g17eco/molecules/question-status/styles.scss';
import { Link } from 'react-router-dom';
import { DATE, formatDateUTC } from '@utils/date';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import {
  DATA_QUALITY_CHECK_LABELS,
  DataQualityIssue,
  DataQualityIssueType,
  VariationIssueDetails,
} from '@api/data-quality';
import { NumberScale } from '@g17eco/types/units';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { VARIATION_DATA_SOURCE_LABEL_MAP } from '@utils/universalTracker';

export interface GroupedIssue {
  utrvId: string;
  initiativeName: string;
  initiativeId: string;
  surveyId?: string;
  utrCode: string;
  utrName: string;
  effectiveDate: string;
  status: string;
  issues: DataQualityIssue[];
  isExpanded: boolean;
}

const getQuestionUrl = (row: DataQualityIssue): string | undefined => {
  if (!row.surveyId) {
    return undefined;
  }
  const baseUrl = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
    initiativeId: row.initiativeId,
    surveyId: row.surveyId,
    page: 'question',
  });
  return `${baseUrl}/${row.utrvId}`;
};

// same text as the CSV export (api variationDetails.ts); the stored code of a value-list group shows in a tooltip
const VariationDetails = ({ details }: { details: VariationIssueDetails }) => {
  const { value, baseline, min, max, dataSource, baselineEffectiveDate, unit, numberScale, group } = details;
  const suffix = [numberScale === NumberScale.Single ? undefined : numberScale, unit].filter(Boolean).join(' ');
  // toFixed drops float noise such as 110.00000000000001
  const format = (num: number) => [Number(num.toFixed(4)), suffix].filter((part) => part !== '').join(' ');
  const date = formatDateUTC(baselineEffectiveDate, DATE.MONTH_YEAR_SHORT);

  return (
    <>
      {group?.map(({ column, value: code, label }, index) => (
        <React.Fragment key={column}>
          {index > 0 ? ', ' : null}
          {`${column} = `}
          {label === code ? label : <SimpleTooltip text={code} className='text-decoration-underline'>{label}</SimpleTooltip>}
        </React.Fragment>
      ))}
      {group ? ': ' : null}
      {`${format(value)} vs baseline ${format(baseline)} (${date}, ${VARIATION_DATA_SOURCE_LABEL_MAP[dataSource]}). `}
      {`Allowed range ${format(min)} to ${format(max)}.`}
    </>
  );
};

interface DataQualityResultsTableProps {
  groupedResults: GroupedIssue[];
  expandedGroups: Set<string>;
  onToggleGroup: (utrvId: string) => void;
  onToggleAll: () => void;
}

export const DataQualityResultsTable = ({
  groupedResults,
  expandedGroups,
  onToggleGroup,
  onToggleAll,
}: DataQualityResultsTableProps) => {
  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <small className='text-muted'>
          {groupedResults.length} metric{groupedResults.length !== 1 ? 's' : ''} with issues
        </small>
        <Button color='link' className='p-0 text-muted' onClick={onToggleAll}>
          {expandedGroups.size === groupedResults.length ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>
      <div className='border rounded overflow-hidden'>
        <table className='table table-hover mb-0'>
          <thead className='table-light'>
            <tr>
              <th style={{ width: '32px' }}></th>
              <th style={{ width: '140px' }}>Reporting Level</th>
              <th>Metric</th>
              <th style={{ width: '180px' }}>Issue Type</th>
              <th style={{ width: '90px' }}>Date</th>
              <th style={{ width: '80px' }}>Status</th>
              <th style={{ width: '80px' }} className='text-end'>Issues</th>
            </tr>
          </thead>
          <tbody>
            {groupedResults.map((group) => {
              const url = getQuestionUrl(group.issues[0]);
              const isExpanded = expandedGroups.has(group.utrvId);
              const issueTypes = [...new Set(group.issues.map((i) => i.issueType))];
              const totalColumns = [...new Set(group.issues.flatMap((i) => i.columns))];

              return (
                <React.Fragment key={group.utrvId}>
                  <tr
                    onClick={() => onToggleGroup(group.utrvId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ width: '32px' }}>
                      <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-muted`} />
                    </td>
                    <td className='text-truncate' style={{ maxWidth: '140px' }} title={group.initiativeName}>
                      {group.initiativeName}
                    </td>
                    <td style={{ maxWidth: '300px' }}>
                      {url ? (
                        <div className='d-flex align-items-center'>
                          <Link
                            to={url}
                            target='_blank'
                            rel='noopener noreferrer'
                            title={`${group.utrName} (${group.utrCode})`}
                            onClick={(e) => e.stopPropagation()}
                            className='text-truncate'
                          >
                            {group.utrName}
                          </Link>
                          <i className='fa fa-external-link-alt fa-sm text-ThemePrimary ms-2 flex-shrink-0' />
                        </div>
                      ) : (
                        <span className='text-truncate d-block' title={`${group.utrName} (${group.utrCode})`}>{group.utrName}</span>
                      )}
                    </td>
                    <td className='text-truncate' style={{ maxWidth: '180px' }} title={issueTypes.map((t) => DATA_QUALITY_CHECK_LABELS[t]).join(', ')}>
                      {issueTypes.map((t) => DATA_QUALITY_CHECK_LABELS[t]).join(', ')}
                    </td>
                    <td style={{ width: '90px', whiteSpace: 'nowrap' }}>{formatDateUTC(group.effectiveDate, DATE.MONTH_YEAR_SHORT)}</td>
                    <td style={{ width: '80px' }}>
                      <div className='badge__wrapper'>
                        <Badge className={`status-info px-2 text-xs ${group.status}`} pill>
                          {group.status}
                        </Badge>
                      </div>
                    </td>
                    <td style={{ width: '80px' }} className='text-end'>
                      <span className='badge bg-warning text-dark'>
                        {group.issues.length} row{group.issues.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                  </tr>
                  {/* Expanded details row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className='bg-light p-0'>
                        <div className='px-3 py-2'>
                          <table className='table table-sm table-bordered mb-0 bg-white'>
                            <thead>
                              <tr>
                                <th style={{ width: '80px' }}>Row #</th>
                                <th style={{ width: '180px' }}>Issue Type</th>
                                <th>Columns</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.issues.map((issue, idx) => (
                                <tr key={`${issue.utrvId}-${issue.rowIndex}-${idx}`}>
                                  <td>{issue.rowIndex}</td>
                                  <td>{DATA_QUALITY_CHECK_LABELS[issue.issueType] ?? issue.issueType}</td>
                                  <td>{issue.columns.join(', ')}</td>
                                  <td>
                                    {issue.issueType === DataQualityIssueType.VariationThreshold && issue.details ? (
                                      <VariationDetails details={issue.details} />
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {totalColumns.length > 0 && (
                            <div className='mt-2'>
                              <small className='text-muted'>
                                All affected columns: {totalColumns.join(', ')}
                              </small>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
